import { Info } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ivaFrequencyForRegime,
  resolveIvaRegimeSelection,
} from "@/lib/fiscalProfileEditorMappers";
import type { FiscalProfileEditorValues } from "@/lib/fiscalProfileEditorTypes";
import type { IraeFrequency, IvaRegime } from "@/services/fiscalService";

export interface TaxConfigurationSectionProps {
  idPrefix: string;
  /** Gratis: only fiscal year end (agenda-related fields hidden). */
  hideAgendaFields?: boolean;
  /** Defaults to 'owner'. 'accountant' swaps "tu" copy for third-person. */
  audience?: "owner" | "accountant";
}

export function TaxConfigurationSection({
  idPrefix,
  hideAgendaFields = false,
  audience = "owner",
}: TaxConfigurationSectionProps): JSX.Element {
  const { t } = useTranslation();
  const { watch, setValue, control } = useFormContext<FiscalProfileEditorValues>();
  const taxes = watch("taxes");
  const audienceKey = audience === "accountant" ? "Acct" : "";
  const isMonotributo = taxes.includes("MONOTRIBUTO");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-foreground">
          {t(
            `settings.fiscalProfile.sections.taxConfigurationTitle${audienceKey}`,
          )}
        </CardTitle>
        <CardDescription>
          {hideAgendaFields
            ? t("settings.fiscalProfile.sections.taxConfigurationDescriptionFree")
            : t(
                `settings.fiscalProfile.sections.taxConfigurationDescription${audienceKey}`,
              )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hideAgendaFields && taxes.includes("IVA") && (() => {
          // Undeclared is not 'minimum' — see resolveIvaRegimeSelection.
          const {
            regime: currentRegime,
            isLockedToMinimum,
            derivedFrequency: derivedFreq,
          } = resolveIvaRegimeSelection(watch("ivaRegime"));

          // Single select drives everything — frequency is derived (read-only
          // line below), and ivaMinimumBenefit is only shown when Literal E.
          // Setting the regime also resets the benefit when we're leaving
          // Literal E, so a tenant flipping CEDE→minimum→CEDE doesn't carry
          // a stale value across.
          const handleRegimeChange = (v: string): void => {
            const next = v as IvaRegime;
            setValue("ivaRegime", next);
            setValue("ivaFrequency", ivaFrequencyForRegime(next));
            if (next !== "minimum") {
              setValue("ivaMinimumBenefit", "");
            } else {
              // Literal E always gets the e-Factura benefit — no choice.
              setValue("ivaMinimumBenefit", "efactura_3_3");
            }
          };

          // isLockedToMinimum above means "IVA mínimo" was ticked in the
          // obligations row: the dropdown shows "Pequeña Empresa" disabled.
          // Otherwise the minimum option is hidden so the accountant has to
          // use the IVA mínimo checkbox to opt in, keeping a single source of
          // truth for Literal E.
          return (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-foreground">
                  {t("settings.fiscalProfile.ivaRegime")}
                </Label>
                <Select
                  value={currentRegime}
                  onValueChange={handleRegimeChange}
                  disabled={isLockedToMinimum}
                >
                  <SelectTrigger className="w-full transition-colors duration-150 sm:max-w-md">
                    <SelectValue
                      placeholder={t(
                        "settings.fiscalProfile.ivaRegimePlaceholder",
                        "Seleccioná un régimen",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {isLockedToMinimum ? (
                      <SelectItem value="minimum">
                        {t(
                          "settings.fiscalProfile.ivaRegimeMinimum",
                          "Pequeña Empresa (Literal E / IVA mínimo)",
                        )}
                      </SelectItem>
                    ) : (
                      <>
                        <SelectItem value="no_cede">
                          {t(
                            "settings.fiscalProfile.ivaRegimeNoCede",
                            "NO CEDE",
                          )}
                        </SelectItem>
                        <SelectItem value="cede">
                          {t("settings.fiscalProfile.ivaRegimeCede", "CEDE")}
                        </SelectItem>
                        <SelectItem value="servicios_personales">
                          {t(
                            "settings.fiscalProfile.ivaRegimeServiciosPersonales",
                            "Servicios Personales (independientes)",
                          )}
                        </SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                {isLockedToMinimum && (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "settings.fiscalProfile.ivaRegimeLockedByMinimum",
                      "Régimen fijado por la opción “IVA mínimo”. Desmarcala arriba para elegir otro régimen.",
                    )}
                  </p>
                )}
                {derivedFreq && (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      `settings.fiscalProfile.ivaFrequencyDescription.${derivedFreq}`,
                      derivedFreq === "minimum"
                        ? "Pago mensual fijo según el monto anual de DGI."
                        : derivedFreq === "monthly"
                          ? "Declaración jurada mensual; vence en el mismo mes."
                          : derivedFreq === "annual"
                            ? "Declaración jurada anual + anticipos mensuales."
                            : "Anticipos bimestrales + declaración jurada anual.",
                    )}
                  </p>
                )}
              </div>

              {currentRegime === "minimum" && (
                <p className="text-xs text-muted-foreground">
                  {t(
                    "settings.fiscalProfile.ivaMinimumBenefitAlways",
                    "Beneficio e-Factura: pagan el 3,3% de lo facturado en el mes, hasta el tope del IVA mínimo. Se aplica automáticamente.",
                  )}
                </p>
              )}

              {/* The 80 UI opt-in used to be a checkbox here. It now comes
                  from the Decreto 206/019 declaration instead: that record
                  carries the motivo (which selects the Art.4 vs Art.5 flow),
                  the declarant and the date, none of which a boolean could,
                  and it is the evidence behind Fiscalo's own Form 2181 filing.
                  The backend no longer accepts fiscalBenefitOptIn from this
                  form at all — it is derived — so a checkbox here would be a
                  control that silently does nothing. */}
            </div>
          );
        })()}

        {/* Monotributo rides the same BPS calendar (vencimiento por último
            dígito del número de empresa), so it needs the number just as much
            as the aportes do. */}
        {!hideAgendaFields &&
          (taxes.includes("BPS") || taxes.includes("MONOTRIBUTO")) && (
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-bps-company-number`} className="text-foreground">
              {t("settings.fiscalProfile.bpsCompanyNumber")}
            </Label>
            <Controller
              name="bpsCompanyNumber"
              control={control}
              render={({ field }) => (
                <Input
                  id={`${idPrefix}-bps-company-number`}
                  value={field.value}
                  onChange={field.onChange}
                  className="font-mono transition-colors duration-150"
                />
              )}
            />
            <p className="text-xs text-muted-foreground">
              {t("settings.fiscalProfile.bpsCompanyNumberHelp")}
            </p>
            {!watch("bpsCompanyNumber").trim() && (
              <div
                role="status"
                className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
              >
                <Info className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <span>{t("settings.fiscalProfile.bpsRequiredForAgenda")}</span>
              </div>
            )}
          </div>
        )}

        {!hideAgendaFields && taxes.includes("IRAE") && (
          <div className="space-y-2">
            <Label className="text-foreground">
              {t("settings.fiscalProfile.iraeFrequency")}
            </Label>
            <Select
              value={watch("iraeFrequency") || "annual"}
              onValueChange={(v) => setValue("iraeFrequency", v as IraeFrequency)}
            >
              <SelectTrigger className="w-full transition-colors duration-150 sm:max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="annual">
                  {t("settings.fiscalProfile.iraeFrequencyAnnual")}
                </SelectItem>
                <SelectItem value="monthly">
                  {t("settings.fiscalProfile.iraeFrequencyMonthly")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-year-end`} className="text-foreground">
              {t("settings.fiscalProfile.fiscalYearEnd")}
            </Label>
            <Controller
              name="fiscalYearEnd"
              control={control}
              render={({ field }) => (
                <Input
                  id={`${idPrefix}-year-end`}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isMonotributo}
                  placeholder={t("settings.fiscalProfile.fiscalYearEndPlaceholder")}
                  className="font-mono transition-colors duration-150"
                />
              )}
            />
            <p className="text-xs text-muted-foreground">
              {isMonotributo
                ? t(
                    "settings.fiscalProfile.fiscalYearEndLockedByMonotributo",
                    "El monotributo no presenta balance: el cierre es siempre el 31/12.",
                  )
                : t("settings.fiscalProfile.fiscalYearEndHelp")}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
